import { useStore } from '@nanostores/react';
import { billing } from '@/services/billing';
import { formatMonthDayYear, formatPeriodRange, knownBoundary, periodDays } from '../utils/billing-period';

export type BillingPeriod = {
  /** Period start, as unix seconds. For queries only: on legacy Stripe it is not the real start. */
  start?: number;
  /** Period start for copy. Absent when the start is not reliable. */
  displayStart?: number;
  /** Period end, as unix seconds. */
  end?: number;
  /** When the period is charged, as unix seconds. */
  chargeDate?: number;
  /**
   * "August 7 – August 31, 2026". Null until both boundaries are known, and null when the start is
   * not reliable.
   */
  range: string | null;
  /** "August 31, 2026". Null until the end is known. */
  endDate: string | null;
  /** False when the start can be later than the real start of the cycle. Copy must not show it then. */
  isStartReliable: boolean;
  /** Counts the first day of the period as day 1. Absent when the start is not reliable. */
  dayNumber?: number;
  totalDays?: number;
  /** False until both boundaries are known. Cards wait for this before they fetch. */
  isResolved: boolean;
};

/**
 * The window the billing tabs report on: the current billing period.
 *
 * The tabs have no range control, so the period is read rather than chosen. They take it from here
 * instead of reading the billing store themselves, so they all cover the same window, and one change
 * swaps in a user-chosen range later. Each tab writes its own copy around these values.
 */
export function useBillingPeriod(): BillingPeriod {
  const start = useStore(billing.billingPeriodStart);
  const end = useStore(billing.billingPeriodEnd);
  const chargeDate = useStore(billing.nextChargeDate);
  const isStartReliable = useStore(billing.hasReliablePeriodStart);

  const range = formatPeriodRange(start, end);
  const days = isStartReliable ? periodDays(start, chargeDate) : null;

  return {
    // Normalised, so a card cannot meter from the epoch on an account that reports 0.
    start: knownBoundary(start),
    displayStart: isStartReliable ? knownBoundary(start) : undefined,
    end: knownBoundary(end),
    chargeDate,
    range: isStartReliable ? range : null,
    endDate: formatMonthDayYear(end),
    isStartReliable,
    dayNumber: days?.dayNumber,
    totalDays: days?.totalDays,
    isResolved: range != null,
  };
}
